<script setup lang="ts">
/**
 * "ปัญหา / ติดตาม" (owner-approved mockup 17 ต.ค. 69): shown only while there is something — an order on its way
 * (DeliveryCard: Lalamove / DHL live, unchanged), a problem the script found (late / failed delivery, payment, website
 * error, claim) or the tag the system put on when the website said the payment failed (why + when).
 * DeliveryCard stays mounted (it polls the carriers while the chat is open); the card is hidden while it has nothing.
 * "รอชำระ" rows (owner 18 ต.ค. 69): orders saved "ยังไม่โอน · ส่ง QR ให้ลูกค้า" the POS still holds (api/app/pos_pending.py, via
 * the deliveries answer) — a warning only once the website's payment deadline passed — and card links "บัตรเครดิต · ส่งลิงก์ให้ลูกค้า"
 * not paid yet (api/app/pos_card.py) — a warning once the link expired.
 */
import { computed } from 'vue'
import type { ConversationDetail } from '../../api/types'
import { autoTagNotes } from '../../lib/autoTags'
import { baht } from '../../lib/inbox'
import { cardDueText, deliveryDateLabel, holdDueText } from '../../lib/order'
import { PROBLEM_LABEL, daysAgoText, isProblemTag, problemsShown } from '../../lib/script'
import { useClock } from '../../stores/clock'
import { useDeliveries } from '../../stores/deliveries'
import { useScript } from '../../stores/script'
import Icon from '../ui/Icon.vue'
import DeliveryCard from './DeliveryCard.vue'

const props = defineProps<{ detail: ConversationDetail }>()
const script = useScript()
const deliveries = useDeliveries()
const clock = useClock()

/** Orders still on their way (delivered ones are shown inside the card only next to a running one). */
const moving = computed(() => (deliveries.data[props.detail.id]?.orders ?? []).some((o) => !o.dim))
const tags = computed(() => (props.detail.tags ?? []).filter(isProblemTag))
const tagNotes = computed(() => autoTagNotes(tags.value, clock.now))
const problems = computed(() => problemsShown(script.scripts[props.detail.id]?.problems ?? [], props.detail.tags))
/** Orders waiting for their payment: held bills in the POS and card links not paid yet. */
const unpaid = computed(() =>
  (deliveries.data[props.detail.id]?.unpaid ?? []).map((u) => {
    const card = u.kind === 'card'
    const due = card ? cardDueText({ expire_at: u.pay_due_at, expired: u.late }) : holdDueText(u)
    const bits = [u.amount !== null ? baht(u.amount) : null, u.delivery_date ? `ส่ง ${deliveryDateLabel(u.delivery_date)}` : null,
      u.code ? (card ? `ลิงก์บัตร ${u.code}` : `พักบิล ${u.code}`) : card ? 'ลิงก์บัตร' : null]
    const late = card ? 'ลิงก์หมดอายุแล้ว ลูกค้าจ่ายไม่ได้' : 'เลยเวลาชำระของรอบนี้แล้ว'
    return { id: u.id, late: u.late, text: bits.filter(Boolean).join(' · '), due: due?.late ? late : (due?.text ?? null) }
  }),
)
/** Something is wrong (a problem the script found, a payment-failed tag, a payment past its deadline) — an order simply
 * on its way is not (owner 18 ต.ค. 69 "ทำไมขึ้นเป็นปัญหา"): then only the delivery shows, without the warning header. */
const trouble = computed(() => problems.value.length > 0 || tagNotes.value.length > 0 || unpaid.value.some((u) => u.late))
const has = computed(() => moving.value || trouble.value || unpaid.value.length > 0)

defineExpose({ has, trouble })
</script>

<template>
  <section
    v-show="has"
    class="lc pc"
    :class="{ warn: trouble }"
    :aria-label="trouble ? 'ปัญหาและติดตาม' : moving ? 'กำลังจัดส่ง' : 'รอชำระ'"
    :aria-hidden="has ? undefined : 'true'"
  >
    <div v-if="trouble" class="lc-h">
      <Icon name="alertCircle" />
      <span class="lc-t">ปัญหา / ติดตาม</span>
    </div>
    <!-- live Lalamove / DHL (unchanged card, without its own section frame) -->
    <DeliveryCard :detail="detail" bare />
    <ul v-if="unpaid.length" class="lc-b pc-list" aria-label="ออเดอร์รอชำระ">
      <li v-for="u in unpaid" :key="`u-${u.id}`" class="pc-i">
        <span class="pc-k" :class="{ crit: u.late }">รอชำระ</span>
        <span class="pc-tx">{{ u.text }}<template v-if="u.due"> · <span :class="{ late: u.late }">{{ u.due }}</span></template></span>
      </li>
    </ul>
    <ul v-if="tagNotes.length || problems.length" class="lc-b pc-list" aria-label="ปัญหาของลูกค้า">
      <li v-for="n in tagNotes" :key="`t-${n.id}`" class="pc-i">
        <span class="tg lg" :class="`tg-${n.tone}`">{{ n.name }}</span>
        <span class="pc-tx">ติดอัตโนมัติ · {{ n.why }}<template v-if="n.when"> · <span class="mono">{{ n.when }}</span></template></span>
      </li>
      <li v-for="(p, i) in problems" :key="`p-${i}`" class="pc-i">
        <span class="pc-k">{{ PROBLEM_LABEL[p.kind] ?? 'ติดตาม' }}</span>
        <span class="pc-tx">{{ p.text }}<template v-if="daysAgoText(p.at, clock.now)"> · {{ daysAgoText(p.at, clock.now) }}</template></span>
      </li>
    </ul>
  </section>
</template>

<style scoped>
.pc.warn {
  border-color: #f0d9a8;
}
.pc.warn .lc-h {
  background: var(--warns);
  color: #5c3700;
}
.pc-list {
  margin: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.pc-i {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 12.5px;
  line-height: 19px;
}
.pc-k {
  flex: none;
  font-weight: 600;
  color: #7a4700;
  white-space: nowrap;
}
.pc-tx {
  min-width: 0;
  color: #3e4d46;
}
.pc-k.crit,
.pc-tx .late {
  color: #a61b12;
}
.tg.lg {
  flex: none;
  height: 22px;
  padding: 0 7px;
  font-size: 12px;
  font-weight: 600;
  line-height: 22px;
}
</style>
